import { useCallback, useState } from 'react';
import type { AdminAccount, AdminDraft } from '@domain/entities';
import { RIGHTS_SCOPES, type RightsMatrix, type RightsScope } from '@domain/value-objects';
import { defaultRights } from '@infrastructure/seed/dataset';
import { useContainer } from '../providers/container-context';
import { useToast } from '../providers/toast-context';
import { useAsync } from '../hooks/use-async';
import { useIsMobile } from '../hooks/use-is-mobile';
import { Checkbox, ConfirmModal, ErrorState, Modal, RefreshButton, StatusBadge, TableSkeleton, Tag, TextField } from '../components/ui';

const SCOPE_LABELS: Record<RightsScope, string> = {
  companies: 'Comptes entreprise',
  offers: 'Offres',
  articles: 'Articles',
  addresses: 'Répertoire d’adresses',
  fruits: 'Suivi de grossesse',
  performance: 'Performance',
  formulas: 'Formules & abonnements',
  countries: 'Pays disponibles',
  users: 'Utilisatrices',
  admins: 'Comptes admin',
};

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super-admin',
  ADMIN: 'Admin',
  EDITOR: 'Éditeur',
  VIEWER: 'Lecteur',
};

function newDraft(): AdminDraft {
  return { name: '', email: '', role: 'ADMIN', rights: defaultRights(), password: '' };
}

export function AdminsPage(): JSX.Element {
  const container = useContainer();
  const toast = useToast();

  const isMobile = useIsMobile();
  const admins = useAsync(() => container.admins.list.execute(), [container]);
  const [editorId, setEditorId] = useState<string | null | undefined>(undefined);
  const [draft, setDraft] = useState<AdminDraft>(newDraft);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState<{ title: string; body: string; confirmLabel?: string; danger?: boolean; fn: () => void } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const openEditor = useCallback((admin: AdminAccount | null) => {
    if (admin === null) {
      setEditorId(null);
      setDraft(newDraft());
      return;
    }
    setEditorId(admin.id);
    setDraft({
      name: admin.name,
      email: admin.email,
      role: admin.role,
      rights: admin.rights ?? defaultRights(),
      password: '',
    });
  }, []);

  const patchAdmin = useCallback(
    (updated: AdminAccount) => {
      admins.patch((list) => {
        const idx = list.findIndex((a) => a.id === updated.id);
        if (idx === -1) return [...list, updated];
        const next = [...list];
        next[idx] = updated;
        return next;
      });
    },
    [admins],
  );

  const save = useCallback(async () => {
    if (editorId === undefined) return;
    setSaving(true);
    try {
      const saved = await container.admins.save.execute({ id: editorId, draft });
      toast.notify(editorId === null ? 'Admin créé' : 'Admin mis à jour');
      setEditorId(undefined);
      patchAdmin(saved);
    } catch (error) {
      toast.notifyError(error);
    } finally {
      setSaving(false);
    }
  }, [container, editorId, draft, toast, patchAdmin]);

  const toggleRole = useCallback(
    async (id: string) => {
      try {
        const updated = await container.admins.toggleRole.execute(id);
        patchAdmin(updated);
      } catch (error) {
        toast.notifyError(error);
      }
    },
    [container, toast, patchAdmin],
  );

  const toggleStatus = useCallback(
    async (admin: AdminAccount) => {
      try {
        const updated = await container.admins.setStatus.execute({
          id: admin.id,
          status: admin.status === 'active' ? 'disabled' : 'active',
        });
        patchAdmin(updated);
      } catch (error) {
        toast.notifyError(error);
      }
    },
    [container, toast, patchAdmin],
  );

  const toggleRight = (scope: RightsScope, key: keyof RightsMatrix[RightsScope]): void => {
    setDraft({
      ...draft,
      rights: {
        ...draft.rights,
        [scope]: { ...draft.rights[scope], [key]: !draft.rights[scope][key] },
      },
    });
  };

  if (admins.loading) return <TableSkeleton rows={3} />;
  if (admins.error) {
    return (
      <ErrorState
        status={admins.error.status}
        message={admins.error.message}
        onRetry={admins.reload}
      />
    );
  }

  const rows = admins.data ?? [];

  return (
    <>
      <div className="bbm-toolbar">
        <div className="bbm-toolbar__start">
          <div className="bbm-toolbar__hint">{rows.length} compte(s) admin</div>
        </div>
        <div className="bbm-toolbar__end">
          <button type="button" className="bbm-btn bbm-btn--primary" onClick={() => openEditor(null)}>
            + Nouvel admin
          </button>
          <RefreshButton onRefresh={admins.reload} loading={admins.loading} />
        </div>
      </div>

      {isMobile ? (
        <div className="bbm-card-stack">
          {rows.map((admin) => (
            <div key={admin.id} className="bbm-mobile-card">
              <div className="bbm-mobile-card__head">
                <div>
                  <div className="bbm-mobile-card__title">{admin.name}</div>
                  <div className="bbm-mobile-card__sub">{admin.email}</div>
                </div>
                <StatusBadge status={admin.status === 'active' ? 'approved' : 'inactive'} label={admin.status === 'active' ? 'Actif' : 'Désactivé'} />
              </div>
              <div className="bbm-mobile-card__fields">
                <div className="bbm-mobile-card__field">
                  <span className="bbm-mobile-card__label">Rôle</span>
                  <Tag background={admin.role === 'SUPER_ADMIN' ? '#D67F9E22' : '#7FB3D522'} color={admin.role === 'SUPER_ADMIN' ? '#8A3F5A' : '#2C5878'}>
                    {ROLE_LABELS[admin.role] ?? admin.role}
                  </Tag>
                </div>
              </div>
              <div className="bbm-mobile-card__actions">
                <button type="button" className="bbm-btn" onClick={() => openEditor(admin)}>Modifier</button>
                <button type="button" className="bbm-btn" onClick={() => setPending({
                  title: 'Changer le rôle ?',
                  body: "Le rôle de l'admin sera modifié immédiatement.",
                  confirmLabel: 'Confirmer',
                  fn: () => void toggleRole(admin.id),
                })}>{admin.role === 'SUPER_ADMIN' ? 'Passer admin' : 'Passer super-admin'}</button>
                <button type="button" className="bbm-btn" onClick={() => setPending({
                  title: 'Modifier le statut ?',
                  body: "Le statut de l'admin sera modifié.",
                  confirmLabel: 'Confirmer',
                  fn: () => void toggleStatus(admin),
                })}>{admin.status === 'active' ? 'Désactiver' : 'Réactiver'}</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bbm-panel">
          <div className="bbm-row bbm-row--head">
            <div style={{ width: 200 }}>Nom</div>
            <div style={{ width: 220 }}>Email</div>
            <div style={{ width: 130 }}>Rôle</div>
            <div style={{ width: 100 }}>Statut</div>
            <div style={{ marginLeft: 'auto' }}>Actions</div>
          </div>
          {rows.map((admin) => (
            <div key={admin.id} className="bbm-row">
              <div className="bbm-cell-strong" style={{ width: 200 }}>
                {admin.name}
              </div>
              <div className="bbm-cell bbm-truncate" style={{ width: 220 }}>
                {admin.email}
              </div>
              <div style={{ width: 130 }}>
                <Tag
                  background={admin.role === 'SUPER_ADMIN' ? '#D67F9E22' : '#7FB3D522'}
                  color={admin.role === 'SUPER_ADMIN' ? '#8A3F5A' : '#2C5878'}
                >
                  {ROLE_LABELS[admin.role] ?? admin.role}
                </Tag>
              </div>
              <div style={{ width: 100 }}>
                <StatusBadge
                  status={admin.status === 'active' ? 'approved' : 'inactive'}
                  label={admin.status === 'active' ? 'Actif' : 'Désactivé'}
                />
              </div>
              <div className="bbm-row__actions">
                <button type="button" className="bbm-btn" onClick={() => openEditor(admin)}>
                  Modifier
                </button>
                <button
                  type="button"
                  className="bbm-btn"
                  onClick={() => setPending({
                    title: 'Changer le rôle ?',
                    body: 'Le rôle de l\'admin sera modifié immédiatement.',
                    confirmLabel: 'Confirmer',
                    fn: () => void toggleRole(admin.id),
                  })}
                >
                  {admin.role === 'SUPER_ADMIN' ? 'Passer admin' : 'Passer super-admin'}
                </button>
                <button
                  type="button"
                  className="bbm-btn"
                  onClick={() => setPending({
                    title: 'Modifier le statut ?',
                    body: 'Le statut de l\'admin sera modifié.',
                    confirmLabel: 'Confirmer',
                    fn: () => void toggleStatus(admin),
                  })}
                >
                  {admin.status === 'active' ? 'Désactiver' : 'Réactiver'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {pending !== null ? (
        <ConfirmModal
          title={pending.title}
          body={pending.body}
          confirmLabel={pending.confirmLabel}
          danger={pending.danger}
          busy={actionBusy}
          onConfirm={() => {
            setActionBusy(true);
            Promise.resolve(pending.fn()).finally(() => {
              setActionBusy(false);
              setPending(null);
            });
          }}
          onCancel={() => setPending(null)}
        />
      ) : null}

      {editorId !== undefined ? (
        <Modal
          title={editorId === null ? 'Nouvel admin' : "Modifier l'admin"}
          width={560}
          onClose={() => setEditorId(undefined)}
          onSubmit={() => void save()}
          busy={saving}
        >
          <TextField
            label="Nom"
            value={draft.name}
            onChange={(value) => setDraft({ ...draft, name: value })}
          />
          <TextField
            label="Email"
            type="email"
            value={draft.email}
            onChange={(value) => setDraft({ ...draft, email: value })}
          />
          <TextField
            label={editorId === null ? 'Mot de passe (min. 12 car.)' : 'Nouveau mot de passe (laisser vide = inchangé)'}
            type="password"
            value={draft.password}
            onChange={(value) => setDraft({ ...draft, password: value })}
          />
          {editorId === null && (
            <p style={{ fontSize: 12, color: '#8A95A3', marginTop: -10, marginBottom: 14 }}>
              Si vide, un mot de passe temporaire sera généré automatiquement.
            </p>
          )}

          <span className="bbm-label">Rôle</span>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
            {(['ADMIN', 'EDITOR', 'VIEWER', 'SUPER_ADMIN'] as const).map((role) => (
              <button
                key={role}
                type="button"
                onClick={() => setDraft({ ...draft, role })}
                style={{
                  padding: '8px 16px',
                  borderRadius: 99,
                  border: 'none',
                  fontSize: 12.5,
                  fontWeight: 700,
                  background: draft.role === role ? '#33506B' : '#F0F2F5',
                  color: draft.role === role ? '#fff' : '#6B7785',
                }}
              >
                {ROLE_LABELS[role]}
              </button>
            ))}
          </div>

          {draft.role !== 'SUPER_ADMIN' ? (
            <>
              <span className="bbm-label">Droits par menu</span>
              <div
                style={{
                  border: '1px solid #E4E8ED',
                  borderRadius: 10,
                  overflow: 'hidden',
                  marginBottom: 20,
                }}
              >
                <div
                  className="bbm-row bbm-row--head"
                  style={{ padding: '9px 14px', gap: 10, fontSize: 11 }}
                >
                  <div style={{ width: 160 }}>Menu</div>
                  <div style={{ width: 70, textAlign: 'center' }}>Créer</div>
                  <div style={{ width: 70, textAlign: 'center' }}>Modifier</div>
                  <div style={{ width: 80, textAlign: 'center' }}>Supprimer</div>
                </div>
                {RIGHTS_SCOPES.map((scope) => (
                  <div
                    key={scope}
                    className="bbm-row"
                    style={{ padding: '9px 14px', gap: 10 }}
                  >
                    <div style={{ width: 160, fontSize: 13, fontWeight: 600 }}>
                      {SCOPE_LABELS[scope]}
                    </div>
                    {(['create', 'edit', 'delete'] as const).map((key) => (
                      <div
                        key={key}
                        style={{
                          width: key === 'delete' ? 80 : 70,
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <Checkbox
                          checked={draft.rights[scope][key]}
                          onChange={() => toggleRight(scope, key)}
                          label={`${SCOPE_LABELS[scope]} — ${key}`}
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </Modal>
      ) : null}
    </>
  );
}
